import { v4 as uuid } from 'uuid';
import { parseNumberInputValue } from './numberInputUtils';

/** Parse MSRP from stored tier JSON; empty/missing stays null (not zero). */
export function parseOptionalMsrpValue(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    const n = parseFloat(String(value));
    return Number.isFinite(n) ? n : null;
}

/**
 * Persisted MSRP tier line (stored on Part as JSON).
 * @typedef {Object} MsrpTierLine
 * @property {string} id
 * @property {'vendor'|'manual'} source
 * @property {number} quantity
 * @property {boolean} [excluded]
 * @property {number|null} [msrpOverride] vendor: override calculated; manual: MSRP
 * @property {number} [unitCost] vendor only (informational when saved)
 */

/**
 * Row shown in Edit Part MSRP table (merged vendor costs + saved lines).
 * @typedef {Object} MsrpTierDisplayRow
 * @property {string} id
 * @property {'vendor'|'manual'} source
 * @property {number} quantity
 * @property {number|null} unitCost
 * @property {number|null} calculatedMsrp
 * @property {boolean} excluded
 * @property {number|null} msrpOverride
 * @property {number|null} effectiveMsrp
 */

export function parseMsrpTierLines(raw) {
    if (!raw) return [];
    let parsed = raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return [];
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
        .map((line) => {
            const quantity = parseNumberInputValue(line.quantity);
            if (!Number.isFinite(quantity) || quantity < 0) return null;
            const source = line.source === 'manual' ? 'manual' : 'vendor';
            const msrpOverride = parseOptionalMsrpValue(line.msrpOverride ?? line.msrp);
            return {
                id: line.id || uuid(),
                source,
                quantity,
                excluded: !!line.excluded,
                msrpOverride,
                unitCost: Number.isFinite(parseNumberInputValue(line.unitCost))
                    ? parseNumberInputValue(line.unitCost)
                    : null,
            };
        })
        .filter(Boolean);
}

export function serializeMsrpTierLines(lines) {
    if (!lines || !lines.length) return null;
    const payload = lines.map((line) => {
        const entry = {
            id: line.id,
            source: line.source,
            quantity: line.quantity,
            excluded: !!line.excluded,
        };
        if (line.source === 'manual') {
            if (Number.isFinite(line.msrpOverride)) {
                entry.msrp = line.msrpOverride;
            }
        } else if (Number.isFinite(line.msrpOverride)) {
            entry.msrpOverride = line.msrpOverride;
        }
        if (line.source === 'vendor' && Number.isFinite(line.unitCost)) {
            entry.unitCost = line.unitCost;
        }
        return entry;
    });
    return JSON.stringify(payload);
}

/** Attach parsed tier lines on part for editing (mutates part). */
export function hydratePartMsrpTierLines(part) {
    if (!part) return;
    const json = part.msrpTiersJson ?? part.msrpTiersJSON ?? null;
    part.msrpTierLines = parseMsrpTierLines(json);
}

export function findSavedMsrpTierLine(savedLines, source, quantity) {
    return (savedLines || []).find(
        (line) => line.source === source && line.quantity === quantity
    );
}

/**
 * Merge default-vendor cost tiers with saved overrides / manual lines.
 * @param {{ kind: string, rows: Array<{ quantity: number, unitCost: number }> }} vendorPreview
 * @param {MsrpTierLine[]} savedLines
 * @param {(unitCost: number) => number} computeRoundedMsrp
 * @returns {MsrpTierDisplayRow[]}
 */
export function buildMsrpTierDisplayRows(vendorPreview, savedLines, computeRoundedMsrp) {
    const saved = savedLines || [];
    const rows = [];
    const usedManualIds = new Set();

    const vendorRows = vendorPreview?.rows || [];
    vendorRows.forEach((vendorRow) => {
        const quantity =
            vendorPreview?.kind === 'fixed'
                ? parseNumberInputValue(vendorRow.quantity) || 1
                : vendorRow.quantity;
        const unitCost = vendorRow.unitCost;
        const calculatedMsrp = computeRoundedMsrp(unitCost);
        const savedLine = findSavedMsrpTierLine(saved, 'vendor', quantity);
        const msrpOverride = savedLine?.msrpOverride ?? null;
        const excluded = savedLine?.excluded ?? false;
        const effectiveMsrp = Number.isFinite(msrpOverride)
            ? msrpOverride
            : Number.isFinite(calculatedMsrp)
                ? calculatedMsrp
                : null;
        rows.push({
            // Vendor rows must keep a stable id across renders even before an override
            // is saved; a random uuid here would remount the input on each keystroke and
            // break id matching in the change handlers (select-all + type would be dropped).
            id: savedLine?.id || `vendor-${quantity}`,
            source: 'vendor',
            quantity,
            unitCost: Number.isFinite(unitCost) ? unitCost : null,
            calculatedMsrp: Number.isFinite(calculatedMsrp) ? calculatedMsrp : null,
            excluded,
            msrpOverride,
            effectiveMsrp,
        });
    });

    saved
        .filter((line) => line.source === 'manual')
        .forEach((line) => {
            if (usedManualIds.has(line.id)) return;
            usedManualIds.add(line.id);
            const msrpOverride = line.msrpOverride;
            rows.push({
                id: line.id,
                source: 'manual',
                quantity: line.quantity,
                unitCost: null,
                calculatedMsrp: null,
                excluded: !!line.excluded,
                msrpOverride,
                effectiveMsrp: Number.isFinite(msrpOverride) ? msrpOverride : null,
            });
        });

    return rows.sort((a, b) => a.quantity - b.quantity);
}

/** Rows included in copy table and customer-facing tier output. */
export function getActiveMsrpTierCopyRows(displayRows) {
    return (displayRows || [])
        .filter((row) => {
            if (row.excluded) return false;
            const price = Number.isFinite(row.effectiveMsrp)
                ? row.effectiveMsrp
                : parseOptionalMsrpValue(row.msrpOverride);
            return price != null && Number.isFinite(price);
        })
        .map((row) => {
            const msrp = Number.isFinite(row.effectiveMsrp)
                ? row.effectiveMsrp
                : parseOptionalMsrpValue(row.msrpOverride);
            return {
                quantity: row.quantity,
                msrp,
            };
        });
}

export function displayRowsToSavedLines(displayRows) {
    return (displayRows || []).map((row) => ({
        id: row.id,
        source: row.source,
        quantity: row.quantity,
        excluded: !!row.excluded,
        msrpOverride: row.msrpOverride,
        unitCost: row.source === 'vendor' ? row.unitCost : null,
    }));
}

export function createManualMsrpTierLine(quantity = 1, msrp = null) {
    const qty = parseNumberInputValue(quantity);
    const price = parseOptionalMsrpValue(msrp);
    return {
        id: uuid(),
        source: 'manual',
        quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
        excluded: false,
        msrpOverride: price,
        unitCost: null,
    };
}

export function updatePartMsrpTierLinesFromDisplay(part, displayRows) {
    if (!part) return;
    part.msrpTierLines = displayRowsToSavedLines(displayRows);
    part.msrpTiersJson = serializeMsrpTierLines(part.msrpTierLines);
}
